// "Move to…" picker (F2 / FR-L1): POST /grackles/{id}/move. Folders organize; they don't grant
// access (folders.rs's module docs), so this list is exactly what the folders API already
// filtered to what the caller can see — nothing here can leak a folder the caller can't see.
import { useEffect, useState } from "react";
import { Dialog, Icon } from "@grackle/ui";
import { ApiFail } from "../api/client";
import { listFolders, moveGrackle } from "./api";
import { flattenFoldersWithDepth } from "./folderTree";
import type { FolderOut } from "../api/types";
import { Notice } from "../components/ui";

export default function MoveToFolderDialog({
  orgId,
  grackleId,
  currentFolderId,
  onClose,
  onMoved,
}: {
  orgId: string;
  grackleId: string;
  /** When known, highlighted as the current location. GrackleOut doesn't carry folder_id today,
   * so the library/home cards pass this as undefined — the list just isn't pre-selected. */
  currentFolderId?: string | null;
  onClose: () => void;
  onMoved: (folderId: string | null) => void;
}) {
  const [folders, setFolders] = useState<FolderOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null); // folder id being moved to, or "" for "No folder"

  useEffect(() => {
    let cancelled = false;
    listFolders(orgId)
      .then((res) => {
        if (!cancelled) setFolders(res.items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiFail ? err.message : "Couldn't load folders.");
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  async function choose(folderId: string | null) {
    setMoving(folderId ?? "");
    setError(null);
    try {
      await moveGrackle(grackleId, folderId);
      onMoved(folderId);
      onClose();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't move this recording.");
      setMoving(null);
    }
  }

  const flat = folders ? flattenFoldersWithDepth(folders) : [];

  return (
    <Dialog open title="Move to…" onClose={onClose} width={360}>
      <div style={{ padding: "4px 12px 16px", maxHeight: 360, overflowY: "auto" }}>
        {error ? (
          <div style={{ padding: "0 12px 8px" }}>
            <Notice tone="error">{error}</Notice>
          </div>
        ) : null}
        <button
          type="button"
          className="gk-menuitem"
          style={{ width: "100%" }}
          disabled={moving !== null}
          onClick={() => void choose(null)}
        >
          <Icon name="x" size={18} />
          <span>No folder{currentFolderId == null ? " (current)" : ""}</span>
        </button>
        {folders === null ? (
          <div className="admin-row-sub" style={{ padding: "10px 12px" }}>
            Loading folders…
          </div>
        ) : flat.length === 0 ? (
          <div className="admin-row-sub" style={{ padding: "10px 12px" }}>
            No folders yet.
          </div>
        ) : (
          flat.map(({ folder, depth }) => (
            <button
              key={folder.id}
              type="button"
              className="gk-menuitem"
              style={{ width: "100%", paddingLeft: 14 + depth * 14 }}
              disabled={moving !== null}
              onClick={() => void choose(folder.id)}
            >
              <Icon name="folder-user" size={18} />
              <span>
                {folder.name}
                {currentFolderId === folder.id ? " (current)" : ""}
              </span>
            </button>
          ))
        )}
      </div>
    </Dialog>
  );
}
